import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, BedDouble, ImagePlus, Lock, LogOut, Pencil, Plus, RotateCcw, Trash2, UserPlus, Users, X } from "lucide-react";
import { AMENITY_DEFS, getBedIcon } from "@/data/rooms";
import { createRoom, createUser, deleteRoom, deleteUser, getToken, invalidateRoomsCache, listUsers, loadRooms, login, logout, resetRooms, seedInitialRooms, slugify, updateRoom, updateUser, uploadRoomPhoto } from "@/data/roomStore";

const inputCls =
    "w-full rounded-xl border border-sand-border bg-white px-4 py-2.5 text-sm text-ink outline-none transition-colors duration-300 focus:border-olive focus:ring-2 focus:ring-olive/20";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted";

const blankRoom = (existing) => ({
    slug: slugify("nueva-habitacion", existing),
    photo: "",
    name: "",
    tagline: "",
    capacity: "",
    beds: "",
    amenityKeys: ["wifi", "tv", "ac", "bath"],
    description: "",
    en: { name: "", tagline: "", capacity: "", beds: "", description: "" },
    gallery: [],
});

function RoomEditor({ room, onSave, onClose }) {
    const [form, setForm] = useState(room);
    const [url, setUrl] = useState("");
    const [error, setError] = useState("");
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setEn = (k, v) => setForm((f) => ({ ...f, en: { ...f.en, [k]: v } }));

    const addPhotos = async (files) => {
        for (const file of files) {
            try {
                const src = await uploadRoomPhoto(file);
                setForm((f) => ({ ...f, gallery: [...f.gallery, { src, alt: file.name || "Foto de la habitación" }] }));
            } catch {
                setError("No se pudo subir una de las fotos (máx. 8 MB, solo imágenes).");
            }
        }
    };

    const addUrl = () => {
        const u = url.trim();
        if (!u) return;
        setForm((f) => ({ ...f, gallery: [...f.gallery, { src: u, alt: form.name || "Foto de la habitación" }] }));
        setUrl("");
    };

    const removePhoto = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));

    const makeCover = (i) =>
        setForm((f) => {
            const g = [...f.gallery];
            const [chosen] = g.splice(i, 1);
            const prev = f.photo;
            const next = [{ src: prev, alt: chosen.alt }, ...g].filter((p) => p.src);
            return { ...f, photo: chosen.src, gallery: next };
        });

    const save = () => {
        if (!form.name.trim()) return setError("Ponle un nombre a la habitación.");
        let { photo, gallery } = form;
        if (!photo && gallery.length) {
            photo = gallery[0].src;
            gallery = gallery.slice(1);
        }
        if (!photo) return setError("Agrega al menos una foto (será la portada).");
        onSave({ ...form, photo, gallery });
    };

    const toggleAmenity = (key) =>
        setForm((f) => ({
            ...f,
            amenityKeys: f.amenityKeys.includes(key) ? f.amenityKeys.filter((k) => k !== key) : [...f.amenityKeys, key],
        }));

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex justify-end bg-olive-deep/60 backdrop-blur-sm"
            data-testid="room-editor"
        >
            <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="h-full w-full max-w-xl overflow-y-auto bg-cream shadow-2xl"
            >
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-sand-border bg-cream/95 px-6 py-4 backdrop-blur">
                    <h2 className="font-display text-2xl font-medium text-ink">
                        {room.name ? `Editar: ${room.name}` : "Nueva habitación"}
                    </h2>
                    <button type="button" onClick={onClose} aria-label="Cerrar editor" data-testid="editor-close-button" className="rounded-full p-2 text-ink-muted hover:bg-sand">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-8 px-6 py-6">
                    <section>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-olive">Datos (español)</h3>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className={labelCls}>Nombre</label>
                                <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="editor-name-input" placeholder="Ej. Habitación Sencilla" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Frase corta</label>
                                    <input className={inputCls} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="editor-tagline-input" placeholder="Ej. Ideal para parejas" />
                                </div>
                                <div>
                                    <label className={labelCls}>Capacidad</label>
                                    <input className={inputCls} value={form.capacity} onChange={(e) => set("capacity", e.target.value)} data-testid="editor-capacity-input" placeholder="Ej. 2 personas" />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Camas</label>
                                <input className={inputCls} value={form.beds} onChange={(e) => set("beds", e.target.value)} data-testid="editor-beds-input" placeholder="Ej. 1 cama king size" />
                            </div>
                            <div>
                                <label className={labelCls}>Descripción</label>
                                <textarea rows="3" className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="editor-description-input" placeholder="Texto de venta de la habitación…" />
                            </div>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-olive">Traducción (inglés)</h3>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className={labelCls}>Name</label>
                                <input className={inputCls} value={form.en.name} onChange={(e) => setEn("name", e.target.value)} data-testid="editor-name-en-input" placeholder="Ej. Single Room" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Tagline</label>
                                    <input className={inputCls} value={form.en.tagline} onChange={(e) => setEn("tagline", e.target.value)} data-testid="editor-tagline-en-input" />
                                </div>
                                <div>
                                    <label className={labelCls}>Capacity</label>
                                    <input className={inputCls} value={form.en.capacity} onChange={(e) => setEn("capacity", e.target.value)} data-testid="editor-capacity-en-input" />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Beds</label>
                                <input className={inputCls} value={form.en.beds} onChange={(e) => setEn("beds", e.target.value)} data-testid="editor-beds-en-input" />
                            </div>
                            <div>
                                <label className={labelCls}>Description</label>
                                <textarea rows="3" className={inputCls} value={form.en.description} onChange={(e) => setEn("description", e.target.value)} data-testid="editor-description-en-input" />
                            </div>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-olive">Amenidades</h3>
                        <div className="mt-4 flex flex-wrap gap-2" data-testid="editor-amenities">
                            {Object.entries(AMENITY_DEFS).map(([key, def]) => {
                                const active = form.amenityKeys.includes(key);
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => toggleAmenity(key)}
                                        aria-pressed={active}
                                        data-testid={`amenity-toggle-${key}`}
                                        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                            active
                                                ? "border-olive bg-olive text-cream"
                                                : "border-sand-border bg-white text-ink-muted hover:border-olive"
                                        }`}
                                    >
                                        <def.icon className="h-4 w-4" strokeWidth={1.5} />
                                        {def.label}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-olive">Fotos</h3>
                        <div className="mt-4">
                            <p className={labelCls}>Portada</p>
                            {form.photo ? (
                                <div>
                                    <img src={form.photo} alt="Portada" data-testid="editor-cover-photo" className="h-40 w-full rounded-xl object-cover" />
                                    <div className="mt-2 flex gap-2">
                                        <label
                                            className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-olive/50 px-3 py-2 text-xs font-semibold text-olive transition-colors hover:bg-sand"
                                            data-testid="editor-cover-change-label"
                                        >
                                            Cambiar portada
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                data-testid="editor-cover-change-input"
                                                onChange={async (e) => {
                                                    const f = e.target.files?.[0];
                                                    if (f) {
                                                        try {
                                                            const src = await uploadRoomPhoto(f);
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                photo: src,
                                                                gallery: prev.photo ? [{ src: prev.photo, alt: "Portada anterior" }, ...prev.gallery] : prev.gallery,
                                                            }));
                                                        } catch {
                                                            setError("No se pudo subir la portada.");
                                                        }
                                                    }
                                                    e.target.value = "";
                                                }}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    photo: "",
                                                    gallery: prev.photo ? [{ src: prev.photo, alt: "Portada anterior" }, ...prev.gallery] : prev.gallery,
                                                }))
                                            }
                                            data-testid="editor-cover-remove-button"
                                            className="flex-1 rounded-xl border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
                                        >
                                            Quitar portada
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex h-40 items-center justify-center rounded-xl border-2 border-dashed border-sand-border text-sm text-ink-muted" data-testid="editor-cover-empty">
                                    Sin portada — la primera foto que agregues será la portada
                                </div>
                            )}
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                {form.gallery.map((p, i) => (
                                    <div key={i} className="group relative overflow-hidden rounded-xl">
                                        <img src={p.src} alt={p.alt} className="h-20 w-full cursor-pointer object-cover" onClick={() => makeCover(i)} title="Clic para hacerla portada" />
                                        <button
                                            type="button"
                                            onClick={() => removePhoto(i)}
                                            aria-label={`Eliminar foto ${i + 1}`}
                                            data-testid={`editor-photo-delete-${i}`}
                                            className="absolute right-1 top-1 rounded-full bg-olive-deep/70 p-1 text-cream opacity-0 transition-opacity group-hover:opacity-100"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 flex flex-col gap-2">
                                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-olive/50 bg-sand px-4 py-3 text-sm font-medium text-olive transition-colors hover:bg-sand-dark" data-testid="editor-photo-upload-label">
                                    <ImagePlus className="h-4 w-4" strokeWidth={1.5} />
                                    Subir fotos desde tu dispositivo
                                    <input type="file" accept="image/*" multiple className="hidden" data-testid="editor-photo-upload-input" onChange={(e) => addPhotos(Array.from(e.target.files || []))} />
                                </label>
                                <div className="flex gap-2">
                                    <input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…o pega la URL de una imagen" data-testid="editor-photo-url-input" />
                                    <button type="button" onClick={addUrl} data-testid="editor-photo-add-button" className="shrink-0 rounded-xl bg-olive px-4 text-sm font-medium text-cream hover:bg-olive-dark">
                                        Agregar
                                    </button>
                                </div>
                                    <p className="text-[11px] font-light text-ink-muted">Las fotos se guardan en Supabase Storage (máx. 8 MB). Haz clic en una miniatura para convertirla en portada.</p>
                            </div>
                        </div>
                    </section>

                    {error && (
                        <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="editor-error">{error}</p>
                    )}

                    <div className="flex gap-3 pb-6">
                        <button type="button" onClick={save} data-testid="editor-save-button" className="flex-1 rounded-full bg-amber py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-amber-dark">
                            Guardar habitación
                        </button>
                        <button type="button" onClick={onClose} data-testid="editor-cancel-button" className="rounded-full border border-sand-border px-6 py-3.5 text-sm font-medium text-ink-muted hover:bg-sand">
                            Cancelar
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}

function AdminLogin({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await login(email, password);
            onSuccess();
        } catch (err) {
            setError(err?.response?.data?.detail || "No se pudo iniciar sesión");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gradient-to-br from-olive-deep via-olive to-olive-dark px-6" data-testid="admin-login">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="w-full max-w-sm rounded-3xl bg-cream p-8 shadow-[0_30px_80px_rgba(45,51,25,0.4)]"
            >
                <div className="flex flex-col items-center">
                    <img src="/fotos/logo-horizontal.png" alt="Hotel Punta Palmeras" width="341" height="120" className="h-12 w-auto" />
                    <p className="mt-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-olive">
                        <Lock className="h-3.5 w-3.5" /> Panel de administración
                    </p>
                </div>
                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
                    <div>
                        <label className={labelCls}>Correo</label>
                        <input type="email" required autoComplete="username" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" placeholder="admin@hotelpuntapalmeras.com" />
                    </div>
                    <div>
                        <label className={labelCls}>Contraseña</label>
                        <input type="password" required autoComplete="current-password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" placeholder="••••••••" />
                    </div>
                    {error && (
                        <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="admin-login-error">{error}</p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit-button"
                        className="w-full rounded-full bg-amber py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-amber-dark disabled:opacity-60"
                    >
                        {loading ? "Entrando…" : "Entrar al panel"}
                    </button>
                </form>
                <Link to="/" className="mt-5 block text-center text-xs font-medium text-ink-muted hover:text-olive">
                    ← Volver al sitio
                </Link>
            </motion.div>
        </div>
    );
}

function UsersTab() {
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({ email: "", name: "", password: "" });
    const [editing, setEditing] = useState(null);
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");

    const load = () => listUsers().then(setUsers).catch(() => setError("No se pudieron cargar los usuarios"));
    useEffect(() => {
        load();
    }, []);

    const notify = (text) => {
        setMsg(text);
        setTimeout(() => setMsg(""), 2500);
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        try {
            if (editing) {
                await updateUser({ id: editing.id, name: form.name, password: form.password });
                notify("Usuario actualizado");
            } else {
                await createUser(form);
                notify("Usuario administrador creado");
            }
            setForm({ email: "", name: "", password: "" });
            setEditing(null);
            load();
        } catch (err) {
            setError(err?.message || "No se pudo guardar el usuario");
        }
    };

    const edit = (user) => {
        setEditing(user);
        setError("");
        setForm({ email: user.email, name: user.name || "", password: "" });
    };

    const remove = async (user) => {
        if (!window.confirm(`¿Eliminar a ${user.email}? Esta acción no se puede deshacer.`)) return;
        setError("");
        try {
            await deleteUser(user.id);
            notify("Usuario eliminado");
            if (editing?.id === user.id) { setEditing(null); setForm({ email: "", name: "", password: "" }); }
            load();
        } catch (err) {
            setError(err?.message || "No se pudo eliminar el usuario");
        }
    };

    return (
        <div className="mt-8 grid gap-6 lg:grid-cols-5" data-testid="admin-users-tab">
            <div className="space-y-3 lg:col-span-3">
                {users.map((u) => (
                    <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center justify-between rounded-2xl bg-cream px-5 py-4 shadow-[0_8px_30px_rgba(45,51,25,0.06)]">
                        <div>
                            <p className="text-sm font-semibold text-ink">{u.name || u.email}</p>
                            <p className="text-xs font-light text-ink-muted">{u.email}</p>
                        </div>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => edit(u)} className="flex items-center gap-1.5 rounded-full border border-olive px-4 py-2 text-xs font-semibold text-olive transition-colors hover:bg-olive hover:text-cream"><Pencil className="h-3.5 w-3.5" /> Editar</button>
                            <button type="button" onClick={() => remove(u)} aria-label={`Eliminar ${u.email}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-400 transition-colors hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                        </div>
                    </div>
                ))}
                {!users.length && !error && <p className="rounded-2xl bg-cream p-4 text-sm text-ink-muted">No hay usuarios para mostrar.</p>}
            </div>
            <form onSubmit={submit} className="h-fit rounded-2xl bg-cream p-6 shadow-[0_8px_30px_rgba(45,51,25,0.06)] lg:col-span-2" data-testid="admin-user-form">
                <h2 className="flex items-center gap-2 font-display text-xl font-medium text-ink">
                    <UserPlus className="h-5 w-5 text-olive" strokeWidth={1.5} />
                    {editing ? "Editar usuario" : "Nuevo usuario"}
                </h2>
                <div className="mt-4 space-y-3">
                    <div>
                        <label className={labelCls}>Correo</label>
                        <input type="email" required disabled={!!editing} className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email-input" />
                    </div>
                    <div>
                        <label className={labelCls}>Nombre</label>
                        <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name-input" />
                    </div>
                    <div>
                        <label className={labelCls}>Contraseña</label>
                        <input type="password" required={!editing} minLength="8" placeholder={editing ? "Deja vacío para conservarla" : ""} className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password-input" />
                    </div>
                    {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="user-form-error">{error}</p>}
                    {msg && <p className="rounded-xl bg-olive/10 px-4 py-3 text-sm font-medium text-olive" data-testid="user-form-success">{msg}</p>}
                    <button type="submit" data-testid="user-form-submit-button" className="w-full rounded-full bg-amber py-3 text-sm font-semibold text-white hover:bg-amber-dark">{editing ? "Guardar cambios" : "Crear usuario"}</button>
                    {editing && <button type="button" onClick={() => { setEditing(null); setForm({ email: "", name: "", password: "" }); }} className="mt-2 w-full text-sm font-medium text-ink-muted hover:text-olive">Cancelar edición</button>}
                </div>
            </form>
        </div>
    );
}

export default function AdminPage() {
    const [rooms, setRooms] = useState([]);
    const [editing, setEditing] = useState(null);
    const [savedMsg, setSavedMsg] = useState(false);
    const [authed, setAuthed] = useState(!!getToken());
    const [tab, setTab] = useState("rooms");

    useEffect(() => {
        if (authed) seedInitialRooms().then(() => loadRooms(true)).then(setRooms).catch(() => {});
    }, [authed]);

    useEffect(() => {
        document.title = "Panel de administración | Hotel Punta Palmeras";
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex";
        document.head.appendChild(meta);
        return () => document.head.removeChild(meta);
    }, []);

    const refresh = async () => {
        invalidateRoomsCache();
        setRooms(await loadRooms(true));
        setSavedMsg(true);
        setTimeout(() => setSavedMsg(false), 2500);
    };

    const onSave = async (room) => {
        try {
            const exists = rooms.some((r) => r.slug === room.slug);
            if (exists) await updateRoom(room.slug, room);
            else await createRoom(room);
            await refresh();
            setEditing(null);
        } catch (e) {
            window.alert(e?.response?.data?.detail || "No se pudo guardar. Intenta de nuevo.");
        }
    };

    const onDelete = async (slug) => {
        if (!window.confirm("¿Eliminar esta habitación del sitio?")) return;
        try {
            await deleteRoom(slug);
            await refresh();
        } catch {
            window.alert("No se pudo eliminar. Intenta de nuevo.");
        }
    };

    const onReset = async () => {
        if (!window.confirm("¿Restaurar las habitaciones originales? Se perderán tus cambios.")) return;
        await resetRooms();
        await refresh();
    };

    if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

    return (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-sand" data-testid="admin-page">
            <header className="sticky top-0 z-10 bg-olive shadow-md">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 md:px-8">
                    <div className="flex items-center gap-4">
                        <img src="/fotos/logo-horizontal.png" alt="Hotel Punta Palmeras" width="341" height="120" className="h-8 w-auto brightness-0 invert" />
                        <span className="hidden text-xs font-medium uppercase tracking-[0.2em] text-cream/70 sm:block">Panel de administración</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-2 rounded-full bg-cream px-4 py-2 text-xs font-semibold text-olive hover:bg-sand">
                            <ArrowLeft className="h-3.5 w-3.5" /> Ver sitio
                        </Link>
                        <button
                            type="button"
                            onClick={() => { logout(); setAuthed(false); }}
                            aria-label="Cerrar sesión"
                            data-testid="admin-logout-button"
                            className="flex items-center gap-2 rounded-full border border-cream/30 px-4 py-2 text-xs font-medium text-cream/85 hover:bg-cream/10"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Salir
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-5 py-10 md:px-8">
                <div className="flex gap-2 rounded-full bg-cream p-1.5 shadow-[0_8px_30px_rgba(45,51,25,0.06)] w-fit" role="tablist" aria-label="Secciones del panel">
                    {[
                        { id: "rooms", label: "Habitaciones" },
                        { id: "users", label: "Usuarios" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                                tab === t.id ? "bg-olive text-cream" : "text-ink-muted hover:text-olive"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "users" ? <UsersTab /> : (
                <>
                <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl font-light tracking-tight text-ink md:text-4xl">Habitaciones</h1>
                        <p className="mt-1 text-sm font-light text-ink-muted">{rooms.length} publicadas en el sitio</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setEditing(blankRoom(rooms.map((r) => r.slug)))}
                        data-testid="admin-add-room-button"
                        className="flex items-center gap-2 rounded-full bg-amber px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-dark"
                    >
                        <Plus className="h-4 w-4" /> Nueva habitación
                    </button>
                </div>

                <AnimatePresence>
                    {savedMsg && (
                        <motion.p
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="mt-4 rounded-xl bg-olive/10 px-4 py-3 text-sm font-medium text-olive"
                            data-testid="admin-saved-message"
                        >
                            Cambios guardados — ya se ven en el sitio.
                        </motion.p>
                    )}
                </AnimatePresence>

                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {rooms.map((room) => {
                        const BedIcon = getBedIcon(room);
                        return (
                            <article key={room.slug} data-testid={`admin-room-${room.slug}`} className="overflow-hidden rounded-2xl bg-cream shadow-[0_8px_30px_rgba(45,51,25,0.08)]">
                                <img src={room.photo} alt={room.name} className="h-40 w-full object-cover" loading="lazy" />
                                <div className="p-5">
                                    <h2 className="font-display text-xl font-medium text-ink">{room.name}</h2>
                                    <div className="mt-2 flex items-center gap-4 text-xs font-light text-ink-muted">
                                        <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-olive" />{room.capacity}</span>
                                        <span className="flex items-center gap-1.5"><BedIcon className="h-3.5 w-3.5 text-olive" />{room.beds}</span>
                                    </div>
                                    <div className="mt-4 flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setEditing(JSON.parse(JSON.stringify(room)))}
                                            data-testid={`admin-edit-${room.slug}`}
                                            className="flex flex-1 items-center justify-center gap-2 rounded-full border border-olive px-4 py-2.5 text-xs font-semibold text-olive transition-colors hover:bg-olive hover:text-cream"
                                        >
                                            <Pencil className="h-3.5 w-3.5" /> Editar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onDelete(room.slug)}
                                            aria-label={`Eliminar ${room.name}`}
                                            data-testid={`admin-delete-${room.slug}`}
                                            className="flex items-center justify-center rounded-full border border-red-200 px-4 py-2.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
                </>
                )}
            </main>

            <AnimatePresence>
                {editing && <RoomEditor room={editing} onSave={onSave} onClose={() => setEditing(null)} />}
            </AnimatePresence>
        </div>
    );
}
